import { useState, useEffect } from 'react';
import { stockService } from '@/services/stock';
import { productService } from '@/services/product';
import { checkOrderAvailability } from '@/lib/operatingHours';
import { orderService } from '@/services/order';
import { cartService } from '@/services/cart';
import { getPickupTimestamp } from '@/lib/dateUtils';
import { checkoutService } from '@/services/checkout';
import type { Restaurant } from '@/services/restaurant';
import type { CheckoutState } from './checkout.types';

interface UseCheckoutValidationProps {
  checkoutData: CheckoutState | null;
  restaurant: Restaurant | null;
  userId: string | undefined;
}

export function useCheckoutValidation({ 
  checkoutData, 
  restaurant, 
  userId 
}: UseCheckoutValidationProps) {
  const [isValidating, setIsValidating] = useState(true);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);

  // 실시간 검증 및 체크아웃 토큰 생성
  useEffect(() => {
    const validateAndPrepareCheckout = async () => {
      if (!checkoutData || !restaurant || !userId) {
        setIsValidating(false);
        return;
      }

      setIsValidating(true);
      setValidationErrors([]);
      const errors: string[] = [];

      try {
        // 1. 재고 검증
        const stockValidation = await stockService.validateCartStock(
          checkoutData.items.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            productName: item.productName
          })),
          checkoutData.orderType
        );

        if (!stockValidation.isValid) {
          errors.push(...stockValidation.errors);
        }

        // 2. 주문 가능 시간 검증
        if (!checkOrderAvailability(restaurant, checkoutData.orderType)) {
          switch (checkoutData.orderType) {
            case 'DEADLINE_DISCOUNT':
              errors.push('마감할인 주문 가능 시간이 아닙니다');
              break;
            case 'RESERVATION_DISCOUNT':
              errors.push('예약할인 주문 마감 시간이 지났습니다');
              break;
            case 'REGULAR_ORDER':
              errors.push('현재 주문 가능 시간이 아닙니다');
              break;
          }
        }

        // 2.5. 마감할인의 경우 당일 등록된 오퍼인지 추가 검증
        if (checkoutData.orderType === 'DEADLINE_DISCOUNT') {
          const today = new Date().toISOString().split('T')[0];
          let hasTodayDeadlineOffer = false;
          
          // 장바구니의 각 상품에 대해 당일 등록된 마감할인 오퍼 확인
          for (const item of checkoutData.items) {
            try {
              const productResult = await productService.getProductById(item.productId);
              if (productResult?.offers) {
                const todayOffer = productResult.offers.find((offer: any) => 
                  offer.offer_type === 'DEADLINE_DISCOUNT' && 
                  offer.is_active &&
                  (offer.valid_from ? 
                    new Date(offer.valid_from).toISOString().split('T')[0] === today :
                    true // valid_from이 없으면 당일 등록으로 간주
                  )
                );
                
                if (todayOffer) {
                  hasTodayDeadlineOffer = true;
                  break;
                }
              }
            } catch (error) {
              console.error(`Error checking product offer for ${item.productId}:`, error);
            }
          }
          
          if (!hasTodayDeadlineOffer) {
            errors.push('당일 등록된 마감할인 상품이 아닙니다. 다시 확인해주세요.');
          }
        }

        // 3. 매장 영업 상태 확인
        if (!restaurant.is_open) {
          errors.push('매장이 현재 영업 중이 아닙니다');
        }

        // 4. 예약할인의 경우 추가 검증
        if (checkoutData.orderType === 'RESERVATION_DISCOUNT') {
          const { getTomorrowOperatingHours } = await import('@/lib/operatingHours');
          const tomorrowHours = getTomorrowOperatingHours(restaurant);
          
          if (tomorrowHours.isClosed) {
            errors.push(`내일(${tomorrowHours.dayName})은 휴무일입니다. 예약할인은 이용할 수 없습니다`);
          } else if (!tomorrowHours.openTime || !tomorrowHours.closeTime) {
            errors.push('내일의 영업시간 정보를 확인할 수 없습니다');
          } else if (!checkoutData.desiredPickupTime) {
            errors.push('예약할인은 희망수령시간을 선택해야 합니다');
          }
        }

        if (errors.length === 0) {
          // 5. 체크아웃 검증 및 토큰 생성
          const currentCart = await cartService.getCurrentCart(userId);
          if (!currentCart) {
            errors.push('장바구니 정보를 찾을 수 없습니다');
          } else {
            // 5-1. 기존 PENDING 주문 확인
            const existingPendingOrder = await orderService.getPendingOrderByCartId(currentCart.id);
            
            if (existingPendingOrder) {
              // 5분 이내면 재사용, 초과면 취소 후 새로 생성
              const elapsed = Date.now() - new Date(existingPendingOrder.created_at).getTime();
              
              if (elapsed < 5 * 60 * 1000) {
                // 5분 이내: 기존 주문 재사용
                console.log('Reusing existing pending order:', existingPendingOrder.id);
                const validationResult = await checkoutService.validateStock(currentCart.id);
                if (validationResult.valid && validationResult.token) {
                  setCheckoutToken(validationResult.token);
                }
                setOrderId(existingPendingOrder.id);
                setValidationErrors(errors);
                setIsValidating(false);
                return; // 기존 주문 재사용, 새로 생성하지 않음
              } else {
                // 5분 초과: 기존 주문 취소
                console.log('Cancelling stale pending order:', existingPendingOrder.id);
                await orderService.cancelPendingOrder(existingPendingOrder.id, '결제 시간 초과');
              }
            }

            // 5-2. 새 주문 생성
            const validationResult = await checkoutService.validateStock(currentCart.id);
            if (validationResult.valid && validationResult.token) {
              setCheckoutToken(validationResult.token);
              
              // 주문 생성 (결제 전)
              const pickupTimestamp = checkoutData.desiredPickupTime 
                ? getPickupTimestamp(checkoutData.orderType, checkoutData.desiredPickupTime)
                : undefined;

              const orderResult = await orderService.createPendingOrderFromCart(userId, {
                cart_id: currentCart.id,
                pickup_time: pickupTimestamp,
                special_requests: '',
                payment_method: 'CARD'
              });

              if (orderResult.success && orderResult.order) {
                setOrderId(orderResult.order.id);
              } else {
                errors.push(orderResult.error || '주문 생성에 실패했습니다');
              }
            } else {
              errors.push(validationResult.message || '재고 검증에 실패했습니다');
            }
          }
        }

        setValidationErrors(errors);
      } catch (error) {
        console.error('Validation error:', error);
        setValidationErrors(['주문 검증 중 오류가 발생했습니다']);
      } finally {
        setIsValidating(false);
      }
    };

    validateAndPrepareCheckout();
  }, [checkoutData, restaurant, userId]);

  return {
    isValidating,
    validationErrors,
    setValidationErrors,
    checkoutToken,
    orderId
  };
}